import {
  composeEvent,
  composeEventCompetitors
} from "@fresh8/ad-data-composition/src/betData";
import {
  injectBrand,
  injectAdTag,
  BrandContext
} from "@fresh8/ad-data-composition/src/brand";
import { injectPreview } from "@fresh8/ad-data-composition/src/preview";
import { Brand } from "@fresh8/types/src";
import { SportCompetitor, SportEvent } from "@fresh8/types/src/betData";
import { Sport, SportEnum } from "@fresh8/types/src/language";
import {
  getColorsForCompetitors,
  TeamColor
} from "@fresh8/ad-data-composition/src/lib/teamColors";
import { areColorsAvailable } from "@fresh8/ad-data-composition/src/lib/setBannerColors";

import { computed, ComputedRef, inject, InjectionKey, provide } from "vue";
import { useTeamJerseys } from "./teamJerseys";

import {
  injectTemplateSize,
  MOBILE_LEADERBOARD
} from "@fresh8/ad-data-composition/src/useTemplateSize";

export const enabledSports: Sport[] = [
  SportEnum.AMERICAN_FOOTBALL,
  SportEnum.BASEBALL,
  SportEnum.BASKETBALL,
  SportEnum.FOOTBALL,
  SportEnum.ICE_HOCKEY
];

export function useTeamBadges(
  eventId: string | undefined,
  brand = injectBrand()
): ComputedRef<boolean> {
  const event = composeEvent(eventId);
  const previewContext = injectPreview();
  const adTagInjected = injectAdTag();
  const operator = computed(() => adTagInjected?.operator.value?._id);
  const sport = computed(() => event.value?.sport as Sport);
  const competition = computed(
    () => event.value && (event.value?.normalisedCompetition as string)
  );

  // Filters Set in Console
  const filters = computed(
    () => brand?.adTemplate?.value?.team?.badge?.filters
  );

  const operatorFilter = computed(() =>
    filters.value?.find(filter => filter.operator == operator.value)
  );

  /**
   * Test various filters, all need to be true for enabling
   *
   * @const teamBadgesEnabled : toggle in console to turn on/off team badges
   *
   * @const isOperatorEnabled: check if specific operator has been enabled in console (on/off toggle in console).
   *
   * @const isSportEnabled: check which sports have been enabled in console, if empty enable all.
   *
   * @const isEnabledSport: check if the sport is an sport that can have teamBranding - see enabledSport const line 10 ^^^
   *
   * @const isCompetitionEnabled: check which sports have been enabled in console, if empty enable all.
   *
   */

  const isOperatorEnabled = computed(() => !!operatorFilter.value);

  const isSportEnabled = computed(() =>
    operatorFilter.value?.sports?.length == 0
      ? true
      : event.value && operatorFilter.value?.sports?.includes(sport.value)
  );

  const isEnabledSport = computed(
    () => enabledSports.findIndex(s => s === event.value?.sport) !== -1
  );

  const isCompetitionEnabled = computed(() =>
    operatorFilter.value?.competitions?.length === 0
      ? true
      : event.value &&
        operatorFilter.value?.competitions?.includes(
          competition.value as string
        )
  );

  const teamBadgesEnabled = computed(
    () => !!brand?.adTemplate?.value?.team?.useTeamBadges
  );

  return computed(() => {
    if (
      !event.value?.competitors.away.name.full &&
      !event.value?.competitors.home.name.full
    ) {
      return false;
    }

    // If being display in the preivew then always display the badges if branding is enabled
    if (teamBadgesEnabled.value && previewContext?.preview.value.isPreview) {
      return true;
    }

    // Filter by sport and operator
    if (
      teamBadgesEnabled.value &&
      !previewContext?.preview.value.isPreview &&
      isOperatorEnabled.value &&
      isSportEnabled.value &&
      isEnabledSport.value &&
      isCompetitionEnabled.value
    ) {
      return true;
    }

    return false;
  });
}

type UseTeamBadgesContext = ReturnType<typeof useTeamBadges>;
const UseTeamBagesSymbol = Symbol("UseTeamBadges");

// Creating the useTeamBadges context to provide down the dependancy tree
// reduces the amount of repeated computed properties are made.
export const provideUseTeamBadges = (eventId: string): void => {
  provide<UseTeamBadgesContext>(UseTeamBagesSymbol, useTeamBadges(eventId));
};

// If the context is undefined when calling the inject, this means provide hasnt been called futher up the tree
export const injectUseTeamBadges = (): UseTeamBadgesContext => {
  return inject<UseTeamBadgesContext>(
    UseTeamBagesSymbol,
    computed(() => false)
  );
};

const TeamColorsKey = Symbol("teamColors");

function teamColors(eventId: string) {
  const event = composeEvent(eventId) as ComputedRef<SportEvent>;
  const brandContext = injectBrand() || ({} as BrandContext);
  const templateSize = injectTemplateSize();

  const isMLBsize = computed(
    () => templateSize?.size.value.name === MOBILE_LEADERBOARD.name
  );
  const competitors = composeEventCompetitors(eventId) as ComputedRef<
    SportCompetitor[]
  >;
  const teamBadgesAvailable = useTeamBadges(eventId, brandContext);
  const teamJerseysAvailable = useTeamJerseys(eventId, brandContext);

  const backgroundColor = computed(() => {
    const brand = brandContext?.brand.value as Brand;
    const brandColors = brand.colors;
    const cardBackgroundColorKey: PropertyKey = isMLBsize.value
      ? brand.adTemplate.backgroundColor
      : brand.adTemplate.cardBackgroundColor;

    const cardBackgroundColor =
      brandColors[cardBackgroundColorKey]?.color || "";

    return cardBackgroundColor;
  });

  const colors = computed(() => {
    return getColorsForCompetitors(
      competitors.value,
      backgroundColor.value,
      event.value.sport
    );
  });

  const teamColorsAvailable = computed(
    () =>
      brandContext.adTemplate.value?.team?.useTeamColors &&
      areColorsAvailable(competitors.value) &&
      colors.value?.home !== undefined &&
      colors.value?.away !== undefined
  );

  const iconColorsAvailable = computed(() => {
    return (
      teamJerseysAvailable.value &&
      (colors.value.home?.helmets || colors.value.home?.jersey) &&
      (colors.value.away?.helmets || colors.value.away?.jersey)
    );
  });

  return {
    colors,
    teamColorsAvailable,
    iconColorsAvailable,
    teamIconsAvailable: computed(() => {
      if (teamBadgesAvailable.value) {
        return [
          event.value?.competitors.away,
          event.value?.competitors.home
        ].every(team => team?.badge !== "");
      }

      if (iconColorsAvailable.value) {
        return true;
      }

      return false;
    })
  };
}

export function provideTeamColors(eventId: string): void {
  provide(TeamColorsKey, teamColors(eventId));
}

export function injectTeamColors(): {
  colors: ComputedRef<{
    home: TeamColor | undefined;
    away: TeamColor | undefined;
  }>;
  teamColorsAvailable: ComputedRef<boolean | undefined>;
  teamIconsAvailable: ComputedRef<boolean | undefined>;
} {
  return inject(TeamColorsKey, {
    colors: computed(() => ({ home: undefined, away: undefined })),
    iconColorsAvailable: computed(() => false),
    teamColorsAvailable: computed(() => false),
    teamIconsAvailable: computed(() => false)
  });
}
